import { api } from './client'

/** Aggregated results posted back by CertSlate Live when a room ends. No per-person data. */
export type LivePollSummary = {
  id: string
  kind: 'choice' | 'text'
  question: string
  options: { text: string; count: number }[]
  totalResponses: number
  answers: string[]
}

/** choice = one right answer; multiple = several can be right (attendees tick all that apply). */
export type LiveQuestionType = 'choice' | 'multiple' | 'truefalse' | 'rating' | 'text'
export type LiveActivityType = 'quiz' | 'confidence' | 'wordcloud' | 'qa' | 'pulse' | 'race'
/** Counts of with-you / not-sure / lost in one round of an understanding check. */
export type LivePulseCounts = { green: number; amber: number; red: number }
/** Quiz: pre | post | feedback. Confidence: before | after. */
export type LiveActivityRole = 'pre' | 'post' | 'feedback' | 'before' | 'after'

export type LiveQuestionSummary = {
  id: string
  type: LiveQuestionType
  text: string
  options: { text: string; count: number }[]
  correctIndex: number | null
  /** Multiple choice: the correct options, and how many people ticked exactly those. */
  correctIndexes?: number[] | null
  fullyCorrect?: number | null
  totalResponses: number
  average: number | null
  distribution: number[]
  answers: string[]
}

/** One activity as it ran, with aggregate results only. */
export type LiveActivitySummary =
  | {
      id: string; type: 'quiz' | 'confidence'; role: LiveActivityRole | null; name: string
      /** A quick poll the trainer made during the session. */
      adhoc?: boolean
      submitted: number; scorePercent: number | null; average: number | null
      questions: LiveQuestionSummary[]
    }
  | { id: string; type: 'wordcloud'; adhoc?: boolean; name: string; prompt: string; submitted: number; words: { word: string; count: number }[] }
  | { id: string; type: 'qa'; name: string; prompt: string; submitted: number; questions: { text: string; votes: number; answered: boolean }[] }
  | { id: string; type: 'pulse'; adhoc?: boolean; name: string; prompt: string; submitted: number; rounds: LivePulseCounts[] }

export type LiveSummary = {
  deliveryId: string
  endedAt: string
  participantCount: number
  activities?: LiveActivitySummary[]
  polls: LivePollSummary[]
}

/** The numbers worth showing at a glance. Any is null when that quiz wasn't run. */
export type LiveHeadline = {
  preAveragePercent: number | null
  postAveragePercent: number | null
  /** People who completed BOTH pre and post — the like-for-like comparison. */
  pairedCount: number | null
  pairedPreAveragePercent: number | null
  pairedPostAveragePercent: number | null
  /** Mean of the feedback form's first rating question (1–10). */
  feedbackAverage: number | null
  feedbackCount: number | null
  /** Confidence before/after: each person's mean rating (1–10), averaged. */
  confidenceBeforeAverage: number | null
  confidenceAfterAverage: number | null
  confidencePairedCount: number | null
  confidencePairedBeforeAverage: number | null
  confidencePairedAfterAverage: number | null
}

// ── Authoring a course's activities ──────────────────────────────────────────

export type LiveQuestion = {
  id: string | null
  type: LiveQuestionType
  text: string
  options: string[] | null
  correctIndex: number | null
  /** Multiple choice: the correct options. */
  correctIndexes: number[] | null
  /** Why the right answer is right, shown when the trainer reveals the answers. Choice, multiple and true/false only. */
  explanation?: string | null
}

export type LiveActivity = {
  id: string | null
  type: LiveActivityType
  name: string
  role: LiveActivityRole | null
  prompt: string | null
  maxWords: number | null
  questions: LiveQuestion[]
  /** Fastest finger only: seconds after which a question closes by itself. Null means the presenter decides. */
  timeLimitSeconds?: number | null
  /** Stand-alone quiz only: ask one question at a time, the whole room answering together, instead of all at once. */
  oneAtATime?: boolean
}

export type CourseLiveActivities = {
  activities: LiveActivity[]
  /** The built-in feedback form used until a quiz takes the feedback role. */
  defaultFeedback: LiveQuestion[]
}

export type LiveDeliveryDto = {
  id: string
  joinCode: string
  joinUrl: string
  status: 'Open' | 'Ended'
  createdAt: string
  endedAt: string | null
  participantCount: number | null
  summary: LiveSummary | null
  headline: LiveHeadline | null
}

export type LiveStatusDto = { enabled: boolean; delivery: LiveDeliveryDto | null }
export type LiveLaunchDto = { delivery: LiveDeliveryDto; presenterUrl: string }

/** Exactly one of the two. */
export type LiveTarget = { bookingId: string; sessionId?: never } | { sessionId: string; bookingId?: never }

export const liveApi = {
  getStatus: (t: LiveTarget) =>
    api.get<LiveStatusDto>('/live/deliveries', { params: { bookingId: t.bookingId, sessionId: t.sessionId } }),
  launch: (t: LiveTarget) =>
    api.post<LiveLaunchDto>('/live/deliveries', { bookingId: t.bookingId ?? null, sessionId: t.sessionId ?? null }),
  presenterLink: (deliveryId: string) =>
    api.post<{ presenterUrl: string }>(`/live/deliveries/${deliveryId}/presenter-link`, {}),
  end: (deliveryId: string) =>
    api.post<LiveDeliveryDto>(`/live/deliveries/${deliveryId}/end`, {}),

  getCourseActivities: (courseId: string) =>
    api.get<CourseLiveActivities>(`/courses/${courseId}/live-activities`),
  saveCourseActivities: (courseId: string, activities: LiveActivity[]) =>
    api.put<CourseLiveActivities>(`/courses/${courseId}/live-activities`, { activities }),
}
